// The builder's Asset Library = admin-curated GLBs (library_assets table) plus
// the built-in kits that live in code (venues, city kit, prefabs, editable
// buildings). Admins can hide a built-in by id from /admin; the hidden ids live
// in library_hidden_builtins.
import { supabase } from '../config/supabase';
import { PREFABS } from '../prefabs';
import { BUILDING_KINDS } from '../buildings';
import { CITY_FLAVORS, cityThumbUrl } from '../cityKit';
import { VENUES, venueThumbUrl } from '../venues';

export interface LibraryAsset {
  id: string;
  name: string;
  category: string;
  tags: string[];
  url: string;
  thumbnail_url: string | null;
  file_size: number | null;
  collidable: boolean;
  hidden: boolean;
  deleted_at: string | null;
  created_at: string;
}

/** A whole room/venue (tag `environment`): placed as the world's environment, not as a prop. */
export function isEnvironmentAsset(a: Pick<LibraryAsset, 'tags'>): boolean {
  return (a.tags || []).includes('environment');
}

/**
 * Placement for an environment asset. The tag `fit:<scale>,<x>,<y>,<z>` undoes
 * the app's environment auto-fit (scale-down past 20 m, recentring onto the
 * floor) so the room lands at its authored size with its floor at y = 0;
 * without it the room is placed as-is at the origin.
 */
export function environmentFit(a: Pick<LibraryAsset, 'tags'>): { scale: number; position: [number, number, number] } {
  const tag = (a.tags || []).find((t) => t.startsWith('fit:'));
  const n = tag ? tag.slice(4).split(',').map(Number) : [];
  if (n.length === 4 && n.every(Number.isFinite) && n[0] > 0) return { scale: n[0], position: [n[1], n[2], n[3]] };
  return { scale: 1, position: [0, 0, 0] };
}

export interface BuiltinItem {
  id: string;        // e.g. "venue:arena"
  group: string;     // Venues / City Kit / Editable Buildings / Prefabs
  title: string;
  thumb?: string;
}

export const builtinId = {
  venue: (id: string) => `venue:${id}`,
  city: (id: string) => `city:${id}`,
  building: (kind: string) => `building:${kind}`,
  prefab: (id: string) => `prefab:${id}`,
};

export function listBuiltins(): BuiltinItem[] {
  const out: BuiltinItem[] = [];
  for (const v of VENUES) out.push({ id: builtinId.venue(v.id), group: 'Venues', title: v.title, thumb: venueThumbUrl(v.id) });
  for (const f of CITY_FLAVORS) for (const b of f.buildings) {
    out.push({ id: builtinId.city(b.id), group: `City Kit — ${f.title}`, title: `${b.label} ${b.variation}`, thumb: cityThumbUrl(b.id) });
  }
  for (const b of BUILDING_KINDS) out.push({ id: builtinId.building(b.kind), group: 'Editable Buildings', title: b.title });
  for (const p of Object.values(PREFABS)) out.push({ id: builtinId.prefab(p.id), group: `Props — ${p.category}`, title: p.title });
  return out;
}

/** Curated library items visible to everyone (RLS hides hidden/deleted for non-admins). */
export async function fetchLibraryAssets(opts: { includeHidden?: boolean } = {}): Promise<LibraryAsset[]> {
  let q = supabase.from('library_assets').select('*').order('category').order('name');
  if (!opts.includeHidden) q = q.eq('hidden', false).is('deleted_at', null);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []) as LibraryAsset[];
}

export async function fetchHiddenBuiltins(): Promise<Set<string>> {
  const { data, error } = await supabase.from('library_hidden_builtins').select('item_id');
  if (error) return new Set();
  return new Set((data || []).map((r: any) => r.item_id as string));
}
